import { CircularProgress } from "@mui/material";
import { message } from "antd";
import axios from "axios";
import React, { createContext, useContext, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { API_BASE_URL } from "../../api";

const EditorContext = createContext();

export function useEditor() {
  return useContext(EditorContext);
}

export function EditorProvider({ children }) {
  // Existing state
  const [focusedClip, setFocusedClip] = useState(null);
  const [clipArray, setClipArray] = useState([]);
  const [cursorPosition, setCursorPos] = useState(0);
  const [loading, setLoading] = useState(false);
  const [audioLength, setAudioLength] = useState(0);
  const cursorRef = React.useRef(55);
  var videoRef = React.useRef(null);
  const [currentTimestamp, setCurrentTimestamp] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [markers, setMarkers] = useState([]);
  const { id } = useParams();
  const [videoData, setVideoData] = useState({
    videoUrl: null,
    videoDuration: null,
    videoThumbnail: null,
  });
  const isFetched = React.useRef(false);

  // New state for enhanced features
  const [cutPoints, setCutPoints] = useState([]);
  const [editMode, setEditMode] = useState("select"); // 'select' or 'cut'
  const [editsApplied, setEditsApplied] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const processorRef = React.useRef(null);

  // Compute segments from cut points
  const getSegments = useCallback(() => {
    if (audioLength <= 0) return [];
    const sorted = [...cutPoints].sort((a, b) => a - b);
    const points = [0, ...sorted, audioLength];
    const segments = [];
    for (let i = 0; i < points.length - 1; i++) {
      if (points[i + 1] - points[i] > 0.1) {
        segments.push({
          id: i,
          startTime: points[i],
          endTime: points[i + 1],
          label: `Segment ${i + 1}`,
        });
      }
    }
    return segments;
  }, [cutPoints, audioLength]);

  async function fetchPost(postId) {
    if (isFetched.current) {
      return;
    }

    setLoading(true);

    try {
      isFetched.current = true;
      const response = await axios.post(
        `${API_BASE_URL}/videos/api/get_video`,
        { video_id: postId },
        {
          withCredentials: true,
          timeout: 50000,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      setLoading(false);
      setAudioLength(response?.data.video?.length);
      setVideoData(response.data);
      return response.data;
    } catch (error) {
      message.error("Error fetching post: " + error.message);
      isFetched.current = false;
      throw new Error("Error fetching post: " + error.message);
    }
  }

  return (
    <EditorContext.Provider
      value={{
        focusedClip,
        setFocusedClip,
        clipArray,
        setClipArray,
        videoData,
        setVideoData,
        fetchPost,
        loading,
        setLoading,
        audioLength,
        cursorPosition,
        setCursorPos,
        cursorRef,
        markers,
        setMarkers,
        zoomLevel,
        setZoomLevel,
        currentTimestamp,
        setCurrentTimestamp,
        id,
        videoRef,
        cutPoints,
        setCutPoints,
        editMode,
        setEditMode,
        editsApplied,
        setEditsApplied,
        isProcessing,
        setIsProcessing,
        processorRef,
        getSegments,
      }}
    >
      {loading ? (
        <div className="flex justify-center items-center h-screen w-screen bg-gray-900">
          <CircularProgress />
        </div>
      ) : (
        children
      )}
    </EditorContext.Provider>
  );
}
